import 'react-image-crop/dist/ReactCrop.css';
import { DeleteOutline, ImageOutlined } from '@mui/icons-material';
import { Box, Button, Divider, InputAdornment } from '@mui/material';
import { useEffect, useState } from 'react';
import ReactImageCrop, { type PercentCrop } from 'react-image-crop';
import { getAuthorizedFileURL } from '../lib/file.ts';
import { ReadOnlyField } from './ReadOnlyField.tsx';

export type ImageInfo = { dataURL: string; crop?: PercentCrop };

type ImageFieldProps = {
  label: string;
  aspect: number;
  imageURL: string | null;
  onChange: (imageInfo: ImageInfo | null) => void;
};

export const ImageField = ({ label, imageURL, aspect, onChange }: ImageFieldProps) => {
  const [previewURL, setPreviewURL] = useState<string | null>(null);

  useEffect(() => {
    if (imageURL) {
      getAuthorizedFileURL(imageURL).then((url) => {
        setPreviewURL(url);
      });
    }
  }, [imageURL]);

  const [crop, setCrop] = useState<PercentCrop>();

  const shouldUpdate =
    // 新しく選択された
    previewURL !== imageURL ||
    // 既存の画像が新たにcropされた
    crop;

  useEffect(() => {
    if (shouldUpdate) {
      onChange(previewURL ? { dataURL: previewURL, crop } : null);
    }
  }, [crop, shouldUpdate, onChange, previewURL]);

  return (
    <ReadOnlyField
      label={label}
      slotProps={{
        inputLabel: {
          shrink: true,
        },
        input: {
          startAdornment: (
            <InputAdornment position="start">
              <ImageOutlined />
            </InputAdornment>
          ),
        },
      }}
    >
      <Box
        component="input"
        type="file"
        accept="image/*"
        sx={{ cursor: 'pointer' }}
        onChange={(e) => {
          if (e.target instanceof HTMLInputElement && e.target.files && e.target.files[0]) {
            if (previewURL) {
              URL.revokeObjectURL(previewURL);
            }

            const file = e.target.files[0];

            setPreviewURL(URL.createObjectURL(file));
          }
        }}
      />
      {previewURL && (
        <Button
          color="error"
          onClick={() => {
            setPreviewURL(null);
          }}
          size="small"
          sx={{ minWidth: 'revert' }}
        >
          <DeleteOutline />
        </Button>
      )}
      {previewURL && (
        <>
          <Divider sx={{ my: 2 }} />
          <CroppableImage
            src={previewURL}
            aspect={aspect}
            style={{
              objectFit: 'cover',
              width: '100%',
              maxWidth: 900,
              height: 'auto',
              margin: '0 auto',
              borderRadius: 4,
              border: '0.5px solid #ccc',
            }}
            alt={label}
            onCrop={(crop) => {
              setCrop(crop);
            }}
          />
        </>
      )}
    </ReadOnlyField>
  );
};

type ImgProps = React.ImgHTMLAttributes<HTMLImageElement>;

type CroppableImageProps = {
  aspect: number;
  onCrop: (crop: PercentCrop) => void;
} & ImgProps;

const CroppableImage = ({ aspect, onCrop, ...imgProps }: CroppableImageProps) => {
  const [crop, _setCrop] = useState<PercentCrop>();
  const setCrop = (crop: PercentCrop) => {
    const normalized = {
      ...crop,
      width: roundPercentage(crop.width),
      height: roundPercentage(crop.height),
      x: roundPercentage(crop.x),
      y: roundPercentage(crop.y),
    };

    if (normalized.width === 100 && normalized.height === 100) {
      // 画像のサイズがクロップのサイズと同じ場合はクロップを設定しない
      _setCrop(undefined);
    } else {
      _setCrop(normalized);
    }
  };

  return (
    <ReactImageCrop
      keepSelection
      aspect={aspect}
      crop={crop}
      onChange={(_, c) => setCrop(c)}
      style={imgProps.style}
      onComplete={(_, crop) => {
        if (crop.width === 0 || crop.height === 0) {
          return;
        }

        onCrop(crop);
      }}
    >
      <img
        {...imgProps}
        alt=""
        onLoad={(e) => {
          if (e.target instanceof HTMLImageElement) {
            const calculatedWidth = Math.round(e.target.clientHeight * aspect);
            const calculatedHeight = Math.round(e.target.clientWidth / aspect);

            const crop =
              e.target.clientHeight >= calculatedHeight
                ? {
                    // width=100%で水平方向に拡大されているので幅を基準に高さを計算する
                    // （クロップが縦に移動する）
                    width: 100,
                    height: (100 * calculatedHeight) / e.target.clientHeight,
                  }
                : {
                    // 水平方向に細長い場合は高さが足りないので高さを基準に幅を計算する
                    // （クロップが横に移動する）
                    height: 100,
                    width: (100 * calculatedWidth) / e.target.clientWidth,
                  };

            // アスペクト比の中で最大値になる初期クロップを設定
            setCrop({
              unit: '%',
              x: 0,
              y: 0,
              ...crop,
            });
          }
        }}
      />
    </ReactImageCrop>
  );
};

function roundPercentage(n: number) {
  return Math.max(0, Math.min(100, Math.round(n)));
}
